'use client'

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void
    Tawk_API?: {
      visitor?: { name?: string; email?: string; phone?: string }
      onLoad?: () => void
      setAttributes?: (attrs: Record<string, string>, cb: (error?: unknown) => void) => void
    }
  }
}

import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase/client'
import { authService, clearAuthSession } from '@/lib/services/auth'
import { Tables } from '@/types/database'
import { sessionCache } from '@/lib/auth/NewSessionCache'
import { isTransientAuthError } from '@/lib/auth/transientAuthError'
import { recoverSessionWithOutcome } from '@/lib/auth/sessionRecovery'

import { logger } from '@/lib/utils/logger'
import { invalidateOnLogout, invalidateProfile } from '@/lib/cache/CacheInvalidation'
import { useQueryClient } from '@tanstack/react-query'
import { analytics } from '@/lib/analytics'

type Profile = Tables<'user_profiles'>

// Key to track which users have had their migrated profiles checked
const MIGRATED_PROFILE_LINKED_KEY = 'migrated_profile_linked'

/**
 * Check if we need to link a migrated profile for this user
 */
async function checkAndLinkMigratedProfile(userId: string): Promise<void> {
  try {
    const linkedUsers = JSON.parse(localStorage.getItem(MIGRATED_PROFILE_LINKED_KEY) || '{}')
    if (linkedUsers[userId]) {
      logger.dev('[Auth] User migrated profile already checked:', userId)
      return
    }

    logger.dev('[Auth] Checking for migrated profile...')

    const response = await fetch('/api/auth/link-migrated-profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })

    const result = await response.json()

    if (response.ok) {
      linkedUsers[userId] = {
        checked: true,
        linked: result.linked,
        timestamp: new Date().toISOString()
      }
      localStorage.setItem(MIGRATED_PROFILE_LINKED_KEY, JSON.stringify(linkedUsers))

      if (result.linked && result.tablesUpdated) {
        logger.dev('[Auth] Successfully linked migrated profile!', result)
      }
    } else {
      logger.error('[Auth] Error checking migrated profile:', result.error)
    }
  } catch (error) {
    logger.error('[Auth] Network error checking migrated profile:', error)
  }
}

interface AuthContextType {
  user: User | null
  profile: Profile | null
  loading: boolean
  profileLoading: boolean
  signOut: () => Promise<void>
  signOutAllDevices: () => Promise<void>
  refreshProfile: (userId?: string, force?: boolean) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
  /**
   * Server-verified auth snapshot from the (main) layout: the user was
   * validated by middleware's getUser() on this very request, the profile
   * fetched server-side. Seeding state from it makes the first client render
   * match the SSR HTML – no Login-button flash, no hydration mismatch, and no
   * zombie sessions (the server, not a local cache, is the source of truth).
   */
  initialUser?: User | null
  initialProfile?: Profile | null
}

export function AuthProvider({ children, initialUser = null, initialProfile = null }: AuthProviderProps) {
  const queryClient = useQueryClient()

  const [user, setUser] = useState<User | null>(initialUser)
  const [profile, setProfile] = useState<Profile | null>(initialProfile)
  // Auth state is known at render time (server snapshot), so there is no
  // loading phase. Kept in the context API for consumers that gate on it.
  const loading = false

  const [profileLoading, setProfileLoading] = useState(false)
  const userIdRef = useRef<string | undefined>(undefined)
  const tawkIdentifiedUserId = useRef<string | null>(null)

  // Update ref whenever user changes
  useEffect(() => {
    userIdRef.current = user?.id
  }, [user?.id])

  const refreshProfile = useCallback(async (targetUserId?: string, force: boolean = false) => {
    const finalUserId = targetUserId || userIdRef.current

    if (!finalUserId) {
      setProfile(null)
      return
    }

    if (force) {
      invalidateProfile(finalUserId)
    }

    // Check cache first (profile cache is still useful – avoids redundant fetches)
    if (!force) {
      const cachedProfile = sessionCache.getProfile(finalUserId)
      if (cachedProfile) {
        setProfile(cachedProfile)
        setProfileLoading(false)
        return
      }
    }

    setProfileLoading(true)

    try {
      const profileData = await authService.getProfile(finalUserId)

      if (profileData) {
        setProfile(profileData)
        sessionCache.setProfile(profileData, finalUserId)
      }
    } catch (error) {
      logger.error('[Auth] Failed to fetch profile:', error)
    } finally {
      setProfileLoading(false)
    }
  }, [])

  // PostHog user identification
  useEffect(() => {
    if (user && profile) {
      const name = [profile.last_name, profile.first_name, profile.patronymic]
        .filter(Boolean)
        .join(' ')

      analytics.identify(user.id, {
        email: profile.email,
        phone: profile.phone ?? undefined,
        name: name || undefined,
        first_name: profile.first_name ?? undefined,
        middle_name: profile.patronymic ?? undefined,
        last_name: profile.last_name ?? undefined,
      })
    }
  }, [user, profile])

  // Microsoft Clarity user identification
  useEffect(() => {
    if (user && profile) {
      const fullName = [profile.last_name, profile.first_name, profile.patronymic]
        .filter(Boolean)
        .join(' ')

      window.clarity?.("identify", user.id, undefined, undefined, fullName)
      window.clarity?.("set", "email", profile.email)
      if (profile.phone) window.clarity?.("set", "phone", profile.phone)
      if (fullName) window.clarity?.("set", "name", fullName)
      window.clarity?.("set", "user_id", user.id)
    }
  }, [user, profile])

  // Tawk.to visitor identification
  useEffect(() => {
    if (user && profile) {
      if (tawkIdentifiedUserId.current === user.id) return

      const fullName = [profile.last_name, profile.first_name, profile.patronymic]
        .filter(Boolean)
        .join(' ')

      let tawkPhone: string | undefined
      if (profile.phone) {
        const cleaned = profile.phone.replace(/[\s\-()]/g, '')
        tawkPhone = cleaned.startsWith('+') ? cleaned
          : cleaned.startsWith('380') ? '+' + cleaned
          : cleaned.startsWith('0') ? '+380' + cleaned.slice(1)
          : '+' + cleaned
      }

      window.Tawk_API = window.Tawk_API || {}
      const visitor: { name: string; email: string; phone?: string } = { name: fullName, email: profile.email }
      if (tawkPhone) visitor.phone = tawkPhone
      window.Tawk_API.visitor = visitor

      const attrs: Record<string, string> = { name: fullName, email: profile.email }
      if (tawkPhone) attrs.phone = tawkPhone

      if (window.Tawk_API?.setAttributes) {
        window.Tawk_API.setAttributes(attrs, (error: unknown) => {
          if (error) logger.error('[Auth] Tawk.to setAttributes error:', error)
        })
      } else {
        const prev = window.Tawk_API?.onLoad
        window.Tawk_API!.onLoad = function () {
          prev?.()
          window.Tawk_API?.setAttributes?.(attrs, () => {})
        }
      }

      tawkIdentifiedUserId.current = user.id
    }
  }, [user, profile])

  // The scope is fixed by which wrapper below is called, never read from the
  // caller's arguments: signOut is handed straight to onClick in places, so a
  // positional scope parameter would silently receive a MouseEvent.
  const performSignOut = useCallback(async (scope: 'local' | 'global') => {
    try {
      await authService.signOut(scope)
    } catch (error) {
      logger.error('[Auth] Sign out error:', error)
    }
    // Always clear state regardless of whether supabase.auth.signOut() succeeded.
    clearAuthSession()
    analytics.reset()
    tawkIdentifiedUserId.current = null
    setUser(null)
    setProfile(null)
    sessionCache.clear()
    invalidateOnLogout()
    queryClient.clear()
  }, [queryClient])

  /** Ends this device's session only. Other devices stay signed in. */
  const signOut = useCallback(() => performSignOut('local'), [performSignOut])

  /** Ends every session on every device, including this one. */
  const signOutAllDevices = useCallback(() => performSignOut('global'), [performSignOut])

  // CORE AUTH LOGIC – the server already verified this request's session
  // (middleware getUser() → layout snapshot → initialUser/initialProfile),
  // so there is no client-side re-validation here. The client only listens
  // for transitions: login, logout, token refresh, cross-tab sync.
  useEffect(() => {
    let mounted = true

    // Seed the profile cache from the server snapshot so the INITIAL_SESSION
    // commit below is a cache hit instead of a duplicate network fetch.
    if (initialUser && initialProfile) {
      sessionCache.setProfile(initialProfile, initialUser.id)
    }

    // Fetch profile and commit both user + profile together, so the UI never
    // renders a state where user is present but profile is still null
    // (which would flash the "Користувач" fallback in the Header).
    const commitAuthenticatedUser = async (authUser: User) => {
      let profileData: Profile | null = sessionCache.getProfile(authUser.id)

      if (!profileData) {
        try {
          profileData = await authService.getProfile(authUser.id)
          if (profileData) {
            sessionCache.setProfile(profileData, authUser.id)
          }
        } catch (err) {
          logger.error('[Auth] Failed to fetch profile:', err)
          // Continue anyway – user is logged in, profile can be null
        }
      }

      if (!mounted) return
      // React batches these together – single render with both values set
      setUser(authUser)
      if (profileData) setProfile(profileData)
    }

    // Server snapshot had a user but the profile fetch timed out server-side –
    // start filling it in immediately instead of waiting for INITIAL_SESSION.
    if (initialUser && !initialProfile) {
      refreshProfile(initialUser.id)
    }

    // Listen for auth changes (login, logout, refresh, cross-tab sync)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return

        logger.dev(`[Auth] Auth state change: ${event}`)

        if (session?.user) {
          sessionCache.set(session)

          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
            checkAndLinkMigratedProfile(session.user.id)
            // Commit user + profile together. Setting the user alone here
            // used to win the race against the init path and flash the
            // "Користувач" fallback until the profile landed.
            //
            // NEVER await Supabase calls inside this callback: auth-js holds
            // the cross-tab Web Lock while awaiting it, so an awaited profile
            // fetch that stalls keeps the lock held and every Supabase query
            // in every tab of the browser profile aborts after
            // lockAcquireTimeout (2026-06-11 incident). setTimeout defers the
            // fetch until after the callback returns and the lock is released.
            const authUser = session.user
            setTimeout(() => { void commitAuthenticatedUser(authUser) }, 0)
          } else if (event === 'TOKEN_REFRESHED') {
            // A refresh gives the same person a new access token. Calling
            // setUser with session.user here replaced the object on every
            // refresh, so every useAuth() consumer re-rendered and every effect
            // keyed on `user` re-ran - including the event page's enrolment
            // check. Only write when the identity really changed.
            setUser((current) => (current?.id === session.user.id ? current : session.user))
          } else if (event === 'USER_UPDATED') {
            logger.dev('[Auth] User updated')
            setUser(session.user)

            const cachedProfile = sessionCache.getProfile(session.user.id)
            if (session.user.email && cachedProfile?.email && session.user.email !== cachedProfile.email) {
              logger.dev('[Auth] Email changed, syncing to user_profiles')
              supabase
                .from('user_profiles')
                .update({ email: session.user.email, updated_at: new Date().toISOString() })
                .eq('id', session.user.id)
                .then(() => refreshProfile(session.user.id, true))
            } else {
              refreshProfile(session.user.id, true)
            }
          } else {
            setUser(session.user)
          }
        } else if (event === 'SIGNED_OUT') {
          analytics.reset()
          setUser(null)
          setProfile(null)
          invalidateOnLogout()
          queryClient.clear()
        }
        // INITIAL_SESSION with no client-side session while a server-verified
        // user is set: keep the server state. The middleware validated the
        // cookie on this very request – a transient local storage/read hiccup
        // must not flip an authenticated UI to logged-out. Real logouts arrive
        // as SIGNED_OUT (handled above) or fail the next token refresh.
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [refreshProfile, initialUser, initialProfile, queryClient])

  // Mobile tab resume: verify session health when the tab becomes visible.
  // On mobile, browsers suspend JS – the SDK's auto-refresh timer can't fire
  // while suspended, so the token may be expired when the user returns.
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible' || !userIdRef.current) return

      logger.dev('[Auth] Tab became visible, verifying session health')

      try {
        const { error } = await supabase.auth.getUser()

        if (error) {
          // Rate limit / upstream outage / aborted request: GoTrue could not
          // answer, which is not evidence that the session is dead. Keep it and
          // let the next navigation revalidate – clearing here is what put a
          // user through 24 sign-ins in 85 minutes on 2026-08-28.
          if (isTransientAuthError(error)) {
            logger.warn('[Auth] Transient auth failure on resume – session preserved:', error.message)
            return
          }

          logger.warn('[Auth] Session invalid on resume, attempting refresh:', error.message)

          // Through recoverSession, never supabase.auth.refreshSession()
          // directly. A direct call here was a fourth uncoordinated refresher:
          // outside the mutex it could race a refresh that was already in
          // flight, lose that race, and clear a session another caller had
          // just recovered - the exact spurious logout this branch exists to
          // remove.
          const outcome = await recoverSessionWithOutcome()

          if (outcome === 'recovered') {
            // Local read: the refresh above already put the fresh session in
            // memory, so this costs no network call and forces no rotation.
            const { data: { session: refreshed } } = await supabase.auth.getSession()
            if (refreshed?.user) {
              logger.dev('[Auth] Session recovered on resume')
              setUser(refreshed.user)
              sessionCache.set(refreshed)
              refreshProfile(refreshed.user.id, true)
            }
          } else if (outcome === 'unavailable') {
            // No verdict: a transient failure, the throttle, or an open
            // breaker. None of those is evidence the session is dead, so keep
            // it and let the next navigation revalidate.
            logger.warn('[Auth] Refresh reached no verdict on resume – session preserved')
          } else {
            logger.warn('[Auth] Session recovery failed on resume – clearing session')
            clearAuthSession()
            sessionCache.clear()
            setUser(null)
            setProfile(null)
            window.dispatchEvent(new CustomEvent('auth:session-restore-failed'))
          }
        }
      } catch (err) {
        logger.error('[Auth] visibilitychange health check error:', err)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo(
    () => ({ user, profile, loading, profileLoading, signOut, signOutAllDevices, refreshProfile }),
    [user, profile, loading, profileLoading, signOut, signOutAllDevices, refreshProfile]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
